import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { API_URL } from 'src/common/utils/api/fetchWrapper';

type ExportParams = {
  includeInternal: boolean;
};

export function useBillingExport() {
  const {
    mutateAsync,
    isPending: isLoading,
    error,
  } = useMutation<void, Error, ExportParams>({
    mutationFn: async ({ includeInternal }) => {
      const response = await fetch(
        `${API_URL}/admin/billing/export?include_internal=${includeInternal}`,
        { method: 'GET', credentials: 'include' },
      );

      if (!response.ok) {
        throw new Error(`Billing export download failed: ${response.statusText}`);
      }

      const disposition = response.headers.get('Content-Disposition') ?? '';
      const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? 'billing.xlsx';
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    },
    onError: () => {
      notifications.show({
        color: 'red',
        message: 'Could not download the billing spreadsheet',
      });
    },
  });

  return {
    downloadExport: (params: ExportParams) => mutateAsync(params).catch(() => undefined),
    isLoading,
    error,
  };
}
